/** Default landing routes after sign-in (when onboarding does not force another page). */
export const ORDER_HISTORY_PATH = '/order-history';
export const TEST_HISTORY_PATH = '/blood-test-summary';
/** When `medical_questionnaire_completed` is false, non–Doctor/Nurse users go here first. */
export const MEDICAL_QUESTIONING_PATH = '/medical-questioning';

function norm(s) {
  return String(s || '').trim().toLowerCase();
}

function normalizeSupervisorViewAs(viewAsRole) {
  const v = viewAsRole != null ? String(viewAsRole).trim() : '';
  if (v === 'Doctor/Nurse' || norm(v) === 'doctor/nurse') return 'Doctor/Nurse';
  return 'Patient';
}

/**
 * Where to send the user after authentication.
 * - Supervisor + view as Doctor/Nurse → orders
 * - Supervisor + view as Patient → test history
 * - Doctor/Nurse → orders
 * - Patient → test history
 *
 * @param {string} actualUserRole - Role from API / session
 * @param {string|null|undefined} supervisorViewAsFromStorage - `supervisorViewAsRole` when already in localStorage
 */
export function getHomePathAfterSignIn(actualUserRole, supervisorViewAsFromStorage) {
  const role = String(actualUserRole || '').trim();

  if (norm(role) === 'supervisor') {
    const viewAs =
      supervisorViewAsFromStorage != null && supervisorViewAsFromStorage !== ''
        ? supervisorViewAsFromStorage
        : typeof localStorage !== 'undefined'
          ? localStorage.getItem('supervisorViewAsRole')
          : null;
    const effective = normalizeSupervisorViewAs(viewAs ?? 'Patient');
    return effective === 'Doctor/Nurse' ? ORDER_HISTORY_PATH : TEST_HISTORY_PATH;
  }

  if (role === 'Doctor/Nurse' || norm(role) === 'doctor/nurse') {
    return ORDER_HISTORY_PATH;
  }

  if (norm(role) === 'patient') {
    return TEST_HISTORY_PATH;
  }

  return TEST_HISTORY_PATH;
}

/** Read persisted questionnaire completion flag (null if unset). */
export function readMedicalQuestionnaireCompletedFromStorage() {
  try {
    const m = localStorage.getItem('medical_questionnaire_completed');
    if (m === 'true') return true;
    if (m === 'false') return false;
  } catch (_) {
    // ignore
  }
  return null;
}

/**
 * Doctor/Nurse flows use orders/prescription; they skip the patient medical questionnaire gate.
 */
export function isDoctorNurseRole(role) {
  const r = String(role || '').trim();
  return r === 'Doctor/Nurse' || norm(r) === 'doctor/nurse';
}

/**
 * After onboarding gates (password, barcode, personal details) pass, choose default landing.
 * Non–Doctor/Nurse users who have not completed the questionnaire (`medical_questionnaire_completed !== true`)
 * are sent to medical questioning first. Completed users go to {@link getHomePathAfterSignIn} (e.g. test history).
 * Users may still open `/medical-questioning` manually at any time; this does not block that route.
 */
export function getPostAuthHomeOrQuestionnaire(
  medicalQuestionnaireCompleted,
  actualUserRole,
  supervisorViewAsFromStorage
) {
  if (
    medicalQuestionnaireCompleted !== true &&
    !isDoctorNurseRole(actualUserRole)
  ) {
    return MEDICAL_QUESTIONING_PATH;
  }
  return getHomePathAfterSignIn(actualUserRole, supervisorViewAsFromStorage);
}
